import type { EmployerSnapshot, PayrollPeriodView, PayslipDelivery } from '@klukka/client';
import { fmt, fmtH, type AdminLang, type PeriodInfo } from '@klukka/core';
import { Ic } from '@klukka/ui';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { Panel, type Tr } from '../widgets';

/**
 * Launavinnsla — turning a period's hours into payments.
 *
 * The figures on this screen are never stored. Every calculation runs against
 * the worklog as it stands at that moment, so correcting somebody's hours and
 * pressing recalculate is the whole of the edit loop, and there is no second
 * copy of the money to fall out of step with the first.
 *
 * Approving is a review decision, not a freeze. It is what puts the payslip in
 * front of the employee, and reopening takes it back — the figures stay live
 * throughout, which is why the note under the buttons says so plainly.
 */
export function PayrollTab({ snap, pinfo, tr, lang }: {
  snap: EmployerSnapshot;
  pinfo: PeriodInfo;
  tr: Tr;
  lang: AdminLang;
}) {
  const [view, setView] = useState<PayrollPeriodView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [emailOn, setEmailOn] = useState(false);

  const periods = periodChoices(snap, pinfo);
  const [period, setPeriod] = useState(periods[0]?.key ?? '');
  const chosen = periods.find((p) => p.key === period) ?? periods[0];
  const range = chosen ? { periodStart: chosen.start, periodEnd: chosen.end } : null;

  useEffect(() => {
    void api.emailAvailable().then(setEmailOn);
  }, []);

  // Changing the period clears the panel rather than leaving the previous
  // period's numbers on screen under a new heading.
  useEffect(() => {
    setView(null);
    setErr(null);
  }, [period]);

  const act = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const calculate = () => act('calc', async () => {
    if (!range) return;
    setView(await api.payrollPeriod(range));
  });

  const download = (label: string, get: () => Promise<Uint8Array>, filename: string) =>
    act(label, async () => {
      const bytes = await get();
      // A blob rather than a link to the API: the PDF needs the bearer token,
      // so the bytes are already here by the time anything is clicked. Copied
      // into a fresh buffer because a Uint8Array over a SharedArrayBuffer is
      // not a valid BlobPart and the return type does not promise which.
      const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    });

  const approved = view?.period.status === 'approved';
  const canNotify = emailOn && approved;

  return (
    <div>
      <Panel style={{ display: 'flex', gap: 18, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="s-fld" style={{ width: 250 }}>
          {tr('payrollPeriod')}
          <select className="inp" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ marginTop: 4 }}>
            {periods.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}{p.approved ? ` · ${tr('runApproved')}` : ''}
              </option>
            ))}
          </select>
        </label>

        <button className="btn-main" disabled={!!busy} onClick={() => void calculate()}>
          <Ic.chart size={15} /> {busy === 'calc' ? tr('generating') : view ? tr('recalcRun') : tr('calcRun')}
        </button>

        {view ? (
          <span
            className="chip"
            style={{
              color: approved ? 'var(--ok)' : 'var(--warn)',
              borderColor: approved ? 'var(--ok)' : 'var(--warn)',
            }}
          >
            {approved ? tr('runApproved') : tr('runDraft')}
          </span>
        ) : (
          <span className="dimtx">{tr('noRun')}</span>
        )}

        {view ? (
          <span className="dimtx" style={{ fontSize: 12 }}>
            {tr('createdBy')} {view.period.createdBy}
            {view.period.approvedAt
              ? ` · ${tr('approvedAt')} ${view.period.approvedAt.slice(0, 10)} · ${view.period.approvedBy}`
              : ''}
          </span>
        ) : null}
      </Panel>

      {err ? (
        <div className="grant-banner" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>{err}</div>
      ) : null}

      {!view || !range ? (
        <Panel><div className="dimtx">{tr('runIntro')}</div></Panel>
      ) : (
        <>
          <div className="kpis">
            <Kpi label={tr('gross')} value={fmt(view.run.totals.gross)} />
            <Kpi label={tr('netPayable')} value={fmt(view.run.totals.net)} accent />
            <Kpi label={tr('taxCol')} value={fmt(view.run.totals.tax)} />
            <Kpi label={tr('fullCost')} value={fmt(view.run.totals.totalCost)} />
          </div>

          <Panel style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className="btn-ghost"
              disabled={!!busy}
              onClick={() => void download(
                'report',
                () => api.payrollReportPdf(range, lang),
                `launakeyrsla-${range.periodStart}.pdf`,
              )}
            >
              <Ic.doc size={15} /> {tr('payrollReport')}
            </button>
            <button
              className="btn-ghost"
              disabled={!!busy}
              onClick={() => void download(
                'batch',
                () => api.payslipBatchPdf(range),
                `launasedlar-${range.periodStart}.pdf`,
              )}
            >
              <Ic.doc size={15} /> {tr('payslipsDraft')}
            </button>

            <span style={{ flex: 1 }} />

            {approved ? (
              <>
                <button
                  className="btn-main"
                  disabled={!!busy || !canNotify}
                  title={canNotify ? undefined : tr('emailOff')}
                  onClick={() => void act('notifyAll', async () => {
                    const { snapshot: _s, ...rest } = await api.notifyPayslips(range);
                    setView({ ...view, period: rest.period, deliveries: rest.deliveries });
                  })}
                >
                  <Ic.inbox size={15} /> {busy === 'notifyAll' ? tr('generating') : tr('notifyAll')}
                </button>
                <button
                  className="btn-ghost"
                  disabled={!!busy}
                  onClick={() => {
                    if (!window.confirm(tr('confirmReopen'))) return;
                    void act('reopen', async () => setView(await api.reopenPayrollPeriod(range)));
                  }}
                >
                  {tr('reopenRun')}
                </button>
              </>
            ) : (
              <button
                className="btn-main"
                disabled={!!busy}
                onClick={() => {
                  if (!window.confirm(tr('confirmApprove'))) return;
                  void act('approve', async () => setView(await api.approvePayrollPeriod(range)));
                }}
              >
                <Ic.sign size={15} /> {tr('approveRun')}
              </button>
            )}
          </Panel>

          <div className="dimtx" style={{ fontSize: 12, margin: '0 0 10px 4px' }}>
            {tr('liveNote')}
            {!emailOn ? ` · ${tr('emailOff')}` : !approved ? ` · ${tr('approveFirst')}` : ''}
          </div>

          <Panel title={`${tr('payroll')} · ${view.run.periodStart} → ${view.run.periodEnd}`}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{tr('name')}</th>
                  <th>{tr('hoursCol')}</th>
                  <th>{tr('gross')}</th>
                  <th>{tr('pensionCol')}</th>
                  <th>{tr('unionFeeCol')}</th>
                  <th>{tr('taxCol')}</th>
                  <th>{tr('net')}</th>
                  <th>{tr('deliveryCol')}</th>
                  <th>{tr('payslipCol')}</th>
                </tr>
              </thead>
              <tbody>
                {view.run.lines.map((l) => {
                  const delivery = [...view.deliveries].reverse().find((d) => d.empId === l.empId);
                  return (
                    <tr key={l.empId}>
                      <td>
                        <b>{l.name}</b>
                        <div className="dimtx" style={{ fontSize: 11 }}>{l.kennitala}</div>
                      </td>
                      <td className="mono">{fmtH(l.totalHours)}</td>
                      <td className="mono">{fmt(l.gross)}</td>
                      <td className="mono">{fmt(l.pensionEmpTotal)}</td>
                      <td className="mono">{fmt(l.unionFee)}</td>
                      <td className="mono">{fmt(l.tax)}</td>
                      <td className="mono"><b>{fmt(l.net)}</b></td>
                      <td><DeliveryCell delivery={delivery} email={l.email} tr={tr} /></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          className="btn-sm"
                          disabled={!!busy}
                          onClick={() => void download(
                            `slip-${l.empId}`,
                            () => api.payslipPdf(range, l.empId),
                            `launasedill-${l.kennitala}-${range.periodStart}.pdf`,
                          )}
                        >
                          <Ic.download size={13} /> {tr('download')}
                        </button>
                        {' '}
                        <button
                          className="btn-sm"
                          disabled={!!busy || !canNotify || !l.email}
                          title={!l.email ? tr('noEmail') : canNotify ? undefined : tr('approveFirst')}
                          onClick={() => void act(`notify-${l.empId}`, async () => {
                            const { snapshot: _s, ...rest } = await api.notifyPayslips(range, l.empId);
                            setView({ ...view, period: rest.period, deliveries: rest.deliveries });
                          })}
                        >
                          <Ic.inbox size={13} /> {busy === `notify-${l.empId}` ? '…' : tr('notifyOne')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="sum">
                  <td>{tr('total')}</td>
                  <td className="mono">{fmtH(view.run.totals.totalHours)}</td>
                  <td className="mono">{fmt(view.run.totals.gross)}</td>
                  <td className="mono">{fmt(view.run.totals.pensionEmpTotal)}</td>
                  <td className="mono">{fmt(view.run.totals.unionFee)}</td>
                  <td className="mono">{fmt(view.run.totals.tax)}</td>
                  <td className="mono"><b>{fmt(view.run.totals.net)}</b></td>
                  <td colSpan={2} />
                </tr>
              </tbody>
            </table>

            <div className="dimtx" style={{ fontSize: 12, marginTop: 10 }}>
              {tr('payrollTaxCol')} {fmt(view.run.totals.trygg)} · {tr('grandTotalCol')}{' '}
              <b>{fmt(view.run.totals.tax + view.run.totals.trygg)}</b>
              {view.run.skipped.length
                ? ` · ${view.run.skipped.length} ${tr('skippedNote')}`
                : ''}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="kpi">
      <div className="kpi-l">{label}</div>
      <div className="kpi-v" style={accent ? { color: 'var(--primary)' } : undefined}>{value}</div>
    </div>
  );
}

function DeliveryCell({ delivery, email, tr }: {
  delivery: PayslipDelivery | undefined;
  email: string;
  tr: Tr;
}) {
  if (!email) return <span className="dimtx" style={{ fontSize: 11 }}>{tr('noEmail')}</span>;
  if (!delivery) return <span className="dimtx" style={{ fontSize: 11 }}>{tr('notSent')}</span>;
  if (delivery.status === 'sent') {
    return (
      <span style={{ color: 'var(--ok)', fontSize: 11 }}>
        {tr('sent')} {delivery.sentAt.slice(0, 10)}
      </span>
    );
  }
  return (
    <span style={{ color: 'var(--danger)', fontSize: 11 }} title={delivery.detail}>
      {tr('sendFailed')}
    </span>
  );
}

/**
 * Periods worth offering: the twelve most recent, plus any that has already
 * been opened, so a period whose calendar has since changed stays reachable.
 */
function periodChoices(snap: EmployerSnapshot, pinfo: PeriodInfo): Array<{
  key: string; label: string; start: string; end: string; approved: boolean;
}> {
  const approved = new Set(
    snap.payrollPeriods.filter((p) => p.status === 'approved').map((p) => p.periodKey),
  );
  const seen = new Map<string, { key: string; label: string; start: string; end: string; approved: boolean }>();
  const add = (start: string, end: string): void => {
    const key = `${start}_${end}`;
    if (!seen.has(key)) {
      seen.set(key, { key, label: `${start} → ${end}`, start, end, approved: approved.has(key) });
    }
  };

  add(pinfo.start, pinfo.end);
  let cursor = pinfo.start;
  for (let i = 0; i < 12; i++) {
    const end = shift(cursor, -1);
    const start = startOfPeriodEnding(end, snap);
    add(start, end);
    cursor = start;
  }
  for (const p of snap.payrollPeriods) add(p.periodStart, p.periodEnd);

  return [...seen.values()].sort((a, b) => b.start.localeCompare(a.start));
}

function shift(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function startOfPeriodEnding(end: string, snap: EmployerSnapshot): string {
  const type = snap.company.settings.periodType;
  const d = new Date(`${end}T12:00:00`);
  if (type === 'week') return shift(end, -6);
  if (type === 'half') return d.getDate() > 15 ? `${end.slice(0, 8)}16` : `${end.slice(0, 8)}01`;
  return `${end.slice(0, 8)}01`;
}
